import React from 'react';
import { CheckCircle, Users, Award, Target, TrendingUp, Brain, Code, Database } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Target className="w-12 h-12" />,
      title: "Our Mission",
      description: "To empower businesses worldwide with cutting-edge AI solutions that drive innovation, efficiency, and sustainable growth."
    },
    {
      icon: <Award className="w-12 h-12" />,
      title: "Our Vision",
      description: "To be the global leader in AI-powered transformation, making advanced technology accessible to organizations of all sizes."
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Our Team",
      description: "50+ expert consultants, data scientists, and engineers dedicated to delivering exceptional results for our clients."
    }
  ];

  const timeline = [
    { year: "2020", title: "Company Founded", description: "Started with a vision to revolutionize AI consulting" },
    { year: "2021", title: "100+ Clients", description: "Reached milestone of serving 100 satisfied clients" },
    { year: "2023", title: "Global Expansion", description: "Opened offices in 5 countries across 3 continents" },
    { year: "2024", title: "500+ Projects", description: "Successfully delivered over 500 AI transformation projects" }
  ];

  const expertise = [
    { icon: <Brain className="w-8 h-8" />, title: "AI & Machine Learning", count: "200+" },
    { icon: <Code className="w-8 h-8" />, title: "Custom Development", count: "150+" },
    { icon: <Database className="w-8 h-8" />, title: "Data Analytics", count: "180+" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden mt-16">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1600&h=900&fit=crop" 
            alt="About Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-cyan-900/70"></div>
        </div>

        <div className="relative container mx-auto px-6 h-full flex items-center">
          <div className="text-white space-y-6">
            <h1 className="text-6xl md:text-7xl font-bold">About Us</h1>
            <p className="text-2xl text-gray-200 max-w-2xl">
              Pioneering AI solutions that transform businesses and drive innovation across industries
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-24 px-6">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="text-sm text-gray-500 tracking-widest">EST. 2025</div>
              <h2 className="text-5xl font-bold text-gray-900">
                Leading the AI Revolution Since 2025
              </h2>
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  We are a forward-thinking AI consulting agency dedicated to helping businesses harness the transformative power of artificial intelligence. Our team of experts combines deep technical knowledge with industry experience to deliver solutions that drive real business value.
                </p>
                <p>
                  From machine learning and neural networks to medical coding automation and data analytics, we provide comprehensive services that address the unique challenges facing modern organizations.
                </p>
                <p>
                  With over 500 successful projects and a 98% client satisfaction rate, we've established ourselves as a trusted partner for businesses seeking to leverage AI for competitive advantage.
                </p>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=600&fit=crop" 
                alt="Team collaboration"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-8 -left-8 bg-white p-8 rounded-2xl shadow-xl">
                <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                  98%
                </div>
                <p className="text-gray-700 font-semibold">Client Satisfaction</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-10 rounded-3xl shadow-lg hover:shadow-xl transition">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center text-white mb-6">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      {/* <section className="py-24 px-6">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-xl text-gray-600">Milestones that shaped our story</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {timeline.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-cyan-400 to-blue-500 text-white p-8 rounded-3xl h-full">
                  <div className="text-5xl font-bold mb-4">{item.year}</div>
                  <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                  <p className="text-blue-100">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Expertise Section */}
      <section className="py-24 px-6 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4">Our Expertise</h2>
            <p className="text-xl text-blue-100">Projects delivered across key domains</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {expertise.map((item, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-lg border border-white/20 p-10 rounded-3xl text-center">
                <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center text-blue-600 mx-auto mb-6">
                  {item.icon}
                </div>
                <div className="text-5xl font-bold text-white mb-3">{item.count}</div>
                <h3 className="text-xl font-semibold text-white">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 px-6">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop" 
                alt="Team working"
                className="rounded-3xl shadow-2xl"
              />
            </div>

            <div className="space-y-8">
              <h2 className="text-5xl font-bold text-gray-900">
                Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Saveratech
        Solutions</span>
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xl text-gray-900 mb-2">Industry-Leading Expertise</h4>
                    <p className="text-gray-600">20+ certified  consultants with proven track records across multiple industries</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xl text-gray-900 mb-2">Customized Solutions</h4>
                    <p className="text-gray-600">Tailored strategies designed specifically for your business needs and goals</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xl text-gray-900 mb-2">Proven Results</h4>
                    <p className="text-gray-600">98% success rate with measurable ROI and business transformation outcomes</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-pink-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xl text-gray-900 mb-2">Ongoing Support</h4>
                    <p className="text-gray-600">Continuous partnership with dedicated support throughout your AI journey</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-r from-cyan-400 to-blue-500">
        <div className="container mx-auto text-center">
          <h2 className="text-5xl font-bold text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join 50+ companies that have already revolutionized their operations with our  solutions
          </p>
          <button className="bg-white text-blue-600 px-12 py-5 rounded-full font-semibold text-lg hover:shadow-2xl transition transform hover:scale-105">
            Get Started Today
          </button>
        </div>
      </section>
    </div>
  );
};

export default About;